import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

// Components
import Page from 'src/components/Page';
import { HeaderPageComponentConfig } from 'src/components/_dashboard/configuracoes';

// Shadcn Components
import { Card, CardContent, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Badge } from 'src/@/components/ui/badge';
import { Progress } from 'src/@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Button } from 'src/@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/@/components/ui/table';

// Icons
import { 
  TrendingUp, 
  TrendingDown, 
  Eye, 
  MousePointer, 
  DollarSign, 
  Users, 
  BarChart3, 
  PieChart, 
  CalendarIcon,
  Home,
  Building,
  Store,
  Clock,
  MapPin,
  UserCheck,
  UserX
} from 'lucide-react';

// Utils
import { fCurrency } from 'src/utils/formatNumber';
import { fDateTimeSuffix } from 'src/utils/formatTime';

// Hooks
import useAuth from 'src/hooks/useAuth';

// Components
import Label from 'src/components/Label';
import Iconify from 'src/components/Iconify';

// Charts
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';

// ----------------------------------------------------------------------

export default function ImobiliariaMetricas() {
  const { user, cliente } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Estados
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [periodo, setPeriodo] = useState('30');
  const [dadosMetricas, setDadosMetricas] = useState(null);
  const [dadosGraficoHorario, setDadosGraficoHorario] = useState([]);
  const [dadosGraficoSemanal, setDadosGraficoSemanal] = useState([]);
  const [dadosGraficoMensal, setDadosGraficoMensal] = useState([]);
  const [dadosBairros, setDadosBairros] = useState([]);
  const [dadosTiposImovel, setDadosTiposImovel] = useState([]);
  const [metricasHorario, setMetricasHorario] = useState(null);
  const [melhorDiaSemana, setMelhorDiaSemana] = useState(null);

  // Buscar dados iniciais
  useEffect(() => {
    buscarDadosMetricas();
    buscarDadosGraficos();
    buscarDadosBairros();
    buscarDadosTiposImovel();
  }, [periodo]);

  const buscarDadosMetricas = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Simular dados de métricas
      const metricas = {
        clientes_unicos: {
          total: 1247,
          atendimentos: 892,
          qualificacoes: 355,
          taxa_qualificacao: 39.8
        },
        leads: {
          total_mes: 456,
          enviados_crm: 312,
          nao_enviados_crm: 144,
          taxa_envio_crm: 68.4
        },
        conversao: {
          leads_para_atendimento: 78.2,
          atendimento_para_qualificacao: 39.8,
          qualificacao_para_venda: 12.4
        }
      };

      setDadosMetricas(metricas);
    } catch (error) {
      console.error('Erro ao buscar métricas:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const buscarDadosGraficos = async () => {
    try {
      // Dados para gráfico de leads por horário (padrão mais realista)
      const horarios = [];
      let totalLeads = 0;
      let leadsForaHorarioComercial = 0;
      
      for (let i = 0; i < 24; i++) {
        let leads = 0;
        
        // Padrão de atividade por horário
        if (i >= 0 && i <= 5) {
          // Madrugada - muito baixa atividade
          leads = Math.floor(Math.random() * 3);
        } else if (i >= 6 && i <= 8) {
          // Manhã cedo - atividade crescente
          leads = Math.floor(Math.random() * 15) + 5;
        } else if (i >= 9 && i <= 11) {
          // Manhã - pico de atividade
          leads = Math.floor(Math.random() * 25) + 20;
        } else if (i >= 12 && i <= 13) {
          // Almoço - queda
          leads = Math.floor(Math.random() * 10) + 5;
        } else if (i >= 14 && i <= 17) {
          // Tarde - alta atividade
          leads = Math.floor(Math.random() * 20) + 15;
        } else if (i >= 18 && i <= 19) {
          // Final da tarde - atividade moderada
          leads = Math.floor(Math.random() * 12) + 8;
        } else {
          // Noite - baixa atividade
          leads = Math.floor(Math.random() * 5) + 1;
        }
        
        // Garantir que às 2h não tenha leads (como no exemplo)
        if (i === 2) {
          leads = 0;
        }
        
        totalLeads += leads;
        
        // Calcular leads fora do horário comercial (antes das 8h e depois das 18h)
        if (i < 8 || i >= 18) {
          leadsForaHorarioComercial += leads;
        }
        
        horarios.push({
          hora: `${i}h`,
          leads: leads,
          atendimentos: Math.floor(leads * 0.7),
          qualificacoes: Math.floor(leads * 0.3)
        });
      }
      
      // Calcular percentual de leads fora do horário comercial
      const percentualForaHorario = totalLeads > 0 ? ((leadsForaHorarioComercial / totalLeads) * 100).toFixed(2) : 0;
      
      setDadosGraficoHorario(horarios);
      
      // Armazenar métrica para exibição
      setMetricasHorario({
        totalLeads,
        leadsForaHorarioComercial,
        percentualForaHorario
      });

      // Dados para gráfico semanal (valores mais realistas)
      const semanas = [
        { dia: 'Dom', leads: 6, nomeCompleto: 'Domingo' },
        { dia: 'Seg', leads: 33, nomeCompleto: 'Segunda-feira' },
        { dia: 'Ter', leads: 41, nomeCompleto: 'Terça-feira' },
        { dia: 'Qua', leads: 32, nomeCompleto: 'Quarta-feira' },
        { dia: 'Qui', leads: 40, nomeCompleto: 'Quinta-feira' },
        { dia: 'Sex', leads: 25, nomeCompleto: 'Sexta-feira' },
        { dia: 'Sáb', leads: 4, nomeCompleto: 'Sábado' }
      ];
      
      // Encontrar o melhor dia da semana
      const melhorDia = semanas.reduce((max, dia) => dia.leads > max.leads ? dia : max, semanas[0]);
      
      setDadosGraficoSemanal(semanas);
      setMelhorDiaSemana(melhorDia);

      // Dados para gráfico mensal (últimos 6 meses) - barras empilhadas
      const meses = [
        { mes: 'Fev', enviados_crm: 0, nao_enviados_crm: 0 },
        { mes: 'Mar', enviados_crm: 0, nao_enviados_crm: 0 },
        { mes: 'Abr', enviados_crm: 88, nao_enviados_crm: 102 },
        { mes: 'Mai', enviados_crm: 249, nao_enviados_crm: 151 },
        { mes: 'Jun', enviados_crm: 214, nao_enviados_crm: 155 },
        { mes: 'Jul', enviados_crm: 96, nao_enviados_crm: 85 }
      ];
      
      setDadosGraficoMensal(meses);

    } catch (error) {
      console.log('Erro ao buscar dados dos gráficos:', error);
    }
  };

  const buscarDadosBairros = async () => {
    try {
      // Dados mock para bairros mais procurados
      const bairros = [
        { nome: 'Centro', cidade: 'São Paulo', leads: 156, cor: '#1f77b4' },
        { nome: 'Vila Madalena', cidade: 'São Paulo', leads: 134, cor: '#ff7f0e' },
        { nome: 'Pinheiros', cidade: 'São Paulo', leads: 128, cor: '#2ca02c' },
        { nome: 'Itaim Bibi', cidade: 'São Paulo', leads: 112, cor: '#d62728' },
        { nome: 'Jardins', cidade: 'São Paulo', leads: 98, cor: '#9467bd' },
        { nome: 'Moema', cidade: 'São Paulo', leads: 87, cor: '#8c564b' },
        { nome: 'Brooklin', cidade: 'São Paulo', leads: 76, cor: '#e377c2' },
        { nome: 'Vila Olímpia', cidade: 'São Paulo', leads: 65, cor: '#7f7f7f' },
        { nome: 'Santo André', cidade: 'Santo André', leads: 54, cor: '#bcbd22' },
        { nome: 'São Bernardo', cidade: 'São Bernardo', leads: 43, cor: '#17becf' },
        { nome: 'Osasco', cidade: 'Osasco', leads: 38, cor: '#ff9896' },
        { nome: 'Guarulhos', cidade: 'Guarulhos', leads: 32, cor: '#98df8a' },
        { nome: 'Barueri', cidade: 'Barueri', leads: 28, cor: '#ffbb78' },
        { nome: 'Santos', cidade: 'Santos', leads: 24, cor: '#c5b0d5' },
        { nome: 'Campinas', cidade: 'Campinas', leads: 21, cor: '#c49c94' }
      ];

      // Ordenar por número de leads (maior para menor)
      bairros.sort((a, b) => b.leads - a.leads);
      
      setDadosBairros(bairros);
    } catch (error) {
      console.log('Erro ao buscar dados dos bairros:', error);
    }
  };

  const buscarDadosTiposImovel = async () => {
    try {
      // Dados mock para tipos de imóvel
      const tiposImovel = [
        { tipo: 'Apartamento', quantidade: 456, percentual: 45.6, cor: '#1f77b4' },
        { tipo: 'Casa', quantidade: 234, percentual: 23.4, cor: '#ff7f0e' },
        { tipo: 'Loja', quantidade: 156, percentual: 15.6, cor: '#2ca02c' },
        { tipo: 'Sala Comercial', quantidade: 89, percentual: 8.9, cor: '#d62728' },
        { tipo: 'Terreno', quantidade: 45, percentual: 4.5, cor: '#9467bd' },
        { tipo: 'Galpão', quantidade: 20, percentual: 2.0, cor: '#8c564b' }
      ];

      setDadosTiposImovel(tiposImovel);
    } catch (error) {
      console.log('Erro ao buscar dados dos tipos de imóvel:', error);
    }
  };

  const handleVoltar = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Page title="Métricas Imobiliária">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-lg text-muted-foreground">Carregando métricas...</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Métricas Imobiliária">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <p className="text-lg text-red-600 mb-2">Erro ao carregar dados</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page title="Métricas Imobiliária">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 shadow-sm border border-green-100">
            <div className="flex items-center justify-between">
              {/* Informações Principais */}
              <div className="flex items-center gap-6">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={handleVoltar}
                  className="hover:bg-white/50 rounded-xl"
                >
                  <Iconify icon={'material-symbols:arrow-back-ios'} height={24} width={24} />
                </Button>
                
                <div className="flex items-center gap-4">
                  {/* Ícone da Imobiliária */}
                  <div className="p-3 rounded-xl shadow-sm bg-green-100">
                    <Home className="h-8 w-8 text-green-600" />
                  </div>
                  
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Métricas Imobiliária</h1>
                    <div className="flex items-center gap-3">
                      <Badge variant="default" className="text-sm px-3 py-1 rounded-full bg-green-600">
                        Ativo
                      </Badge>
                      <span className="text-sm font-semibold px-3 py-1 rounded-full bg-white/70 text-green-700">
                        🏠 Imobiliária
                      </span>
                      <span className="text-sm text-gray-600 bg-white/50 px-3 py-1 rounded-full">
                        📅 Últimos {periodo} dias
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Seletor de Período */}
              <div className="flex items-center gap-4">
                <Select value={periodo} onValueChange={setPeriodo}>
                  <SelectTrigger className="w-32 bg-white/70">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 dias</SelectItem>
                    <SelectItem value="30">30 dias</SelectItem>
                    <SelectItem value="90">90 dias</SelectItem>
                    <SelectItem value="365">1 ano</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Métricas de Clientes Únicos */}
        <div className="mb-8">
          <div className="bg-slate-100 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <Users className="h-6 w-6 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-800">Clientes Únicos</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total de Clientes */}
              <Card className="hover:shadow-lg transition-all duration-300 border-0 bg-gradient-to-br from-blue-50 to-blue-100">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-700 mb-2">Total de Clientes</p>
                      <p className="text-3xl font-bold text-blue-900">
                        {dadosMetricas?.clientes_unicos?.total?.toLocaleString('pt-BR') || 0}
                      </p>
                    </div>
                    <div className="p-4 bg-blue-200 rounded-xl">
                      <Users className="h-8 w-8 text-blue-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Atendimentos */}
              <Card className="hover:shadow-lg transition-all duration-300 border-0 bg-gradient-to-br from-green-50 to-green-100">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-700 mb-2">Atendimentos</p>
                      <p className="text-3xl font-bold text-green-900">
                        {dadosMetricas?.clientes_unicos?.atendimentos?.toLocaleString('pt-BR') || 0}
                      </p>
                    </div>
                    <div className="p-4 bg-green-200 rounded-xl">
                      <UserCheck className="h-8 w-8 text-green-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Qualificações */}
              <Card className="hover:shadow-lg transition-all duration-300 border-0 bg-gradient-to-br from-purple-50 to-purple-100">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-purple-700 mb-2">Qualificações</p>
                      <p className="text-3xl font-bold text-purple-900">
                        {dadosMetricas?.clientes_unicos?.qualificacoes?.toLocaleString('pt-BR') || 0}
                      </p>
                    </div>
                    <div className="p-4 bg-purple-200 rounded-xl">
                      <TrendingUp className="h-8 w-8 text-purple-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Taxa de Qualificação */}
              <Card className="hover:shadow-lg transition-all duration-300 border-0 bg-gradient-to-br from-orange-50 to-orange-100">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-orange-700 mb-2">Taxa de Qualificação</p>
                      <p className="text-3xl font-bold text-orange-900">
                        {dadosMetricas?.clientes_unicos?.taxa_qualificacao?.toFixed(1) || 0}%
                      </p>
                    </div>
                    <div className="p-4 bg-orange-200 rounded-xl">
                      <BarChart3 className="h-8 w-8 text-orange-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Gráficos de Leads */}
        <div className="mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Gráfico de Leads por Horário */}
            <Card className="border-0 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Clock className="h-6 w-6 text-blue-600" />
                    </div>
                    Leads por horário em julho
                  </CardTitle>
                  
                  {/* Dropdown de Visão Geral */}
                  <Select defaultValue="visao-geral">
                    <SelectTrigger className="w-32 bg-gray-50 border-gray-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="visao-geral">Visão Geral</SelectItem>
                      <SelectItem value="detalhado">Detalhado</SelectItem>
                      <SelectItem value="comparativo">Comparativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Métrica Destacada */}
                {metricasHorario && (
                  <div className="mt-4">
                    <div className="text-4xl font-bold text-gray-900 mb-2">
                      {metricasHorario.percentualForaHorario}%
                    </div>
                    <p className="text-sm text-gray-600">
                      leads foram atendidos fora do horário comercial em julho
                    </p>
                  </div>
                )}
                
                {/* Callout para horário específico */}
                <div className="mt-3">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                    Nenhum lead chegou às 2h
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dadosGraficoHorario}>
                      <defs>
                        <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.05}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="hora" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#666' }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#666' }}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="leads" 
                        stroke="#8b5cf6" 
                        strokeWidth={2}
                        fill="url(#colorLeads)"
                        dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Gráfico de Leads Semanais */}
            <Card className="border-0 bg-gradient-to-br from-green-50 to-emerald-50 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CalendarIcon className="h-6 w-6 text-green-600" />
                    </div>
                    Leads semanais em julho
                  </CardTitle>
                  
                  {/* Dropdown de Visão Geral */}
                  <Select defaultValue="visao-geral">
                    <SelectTrigger className="w-32 bg-gray-50 border-gray-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="visao-geral">Visão Geral</SelectItem>
                      <SelectItem value="detalhado">Detalhado</SelectItem>
                      <SelectItem value="comparativo">Comparativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Melhor Dia Destacado */}
                {melhorDiaSemana && (
                  <div className="mt-4">
                    <div className="text-4xl font-bold text-gray-900 mb-2">
                      {melhorDiaSemana.nomeCompleto}
                    </div>
                    <p className="text-sm text-gray-600">
                      Melhor dia da semana em julho
                    </p>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dadosGraficoSemanal}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="dia" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#666' }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#666' }}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                        formatter={(value) => [value, 'Leads']}
                      />
                      <Bar 
                        dataKey="leads" 
                        fill="#8b5cf6"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Gráfico de Leads Últimos Meses (CRM) */}
        <div className="mb-8">
          <Card className="border-0 bg-gradient-to-br from-purple-50 to-violet-50 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Clock className="h-6 w-6 text-purple-600" />
                  </div>
                  Leads nos últimos 6 meses
                </CardTitle>
                
                {/* Dropdown de Visão Geral */}
                <Select defaultValue="visao-geral">
                  <SelectTrigger className="w-32 bg-gray-50 border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visao-geral">Visão Geral</SelectItem>
                    <SelectItem value="detalhado">Detalhado</SelectItem>
                    <SelectItem value="comparativo">Comparativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Legenda */}
              <div className="flex items-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-700"></div>
                  <span className="text-sm text-gray-600">Enviado ao CRM</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-300"></div>
                  <span className="text-sm text-gray-600">Não enviado ao CRM</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dadosGraficoMensal}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="mes" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Bar dataKey="enviados_crm" stackId="a" fill="#7c3aed" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="nao_enviados_crm" stackId="a" fill="#c4b5fd" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bairros Mais Procurados e Tipos de Imóvel */}
        <div className="mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Lista de Bairros Mais Procurados */}
            <Card className="border-0 bg-gradient-to-br from-slate-50 to-gray-50 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <MapPin className="h-6 w-6 text-green-600" />
                  </div>
                  Bairros Mais Procurados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-80 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-200">
                        <TableHead className="w-12 text-gray-600 font-semibold">#</TableHead>
                        <TableHead className="text-gray-600 font-semibold">Bairro</TableHead>
                        <TableHead className="text-gray-600 font-semibold">Cidade</TableHead>
                        <TableHead className="text-right text-gray-600 font-semibold">Leads</TableHead>
                        <TableHead className="text-right text-gray-600 font-semibold">%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dadosBairros.map((bairro, index) => {
                        const totalLeads = dadosBairros.reduce((sum, b) => sum + b.leads, 0);
                        const percentual = ((bairro.leads / totalLeads) * 100).toFixed(1);
                        
                        return (
                          <TableRow key={bairro.nome} className="hover:bg-gray-50 transition-colors">
                            <TableCell className="font-medium">
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-sm"
                                style={{ backgroundColor: bairro.cor }}
                              >
                                {index + 1}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="font-semibold text-gray-900">{bairro.nome}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-gray-500">{bairro.cidade}</div>
                            </TableCell>
                            <TableCell className="text-right font-bold text-gray-900">
                              {bairro.leads.toLocaleString('pt-BR')}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-3">
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="h-2 rounded-full transition-all duration-500" 
                                    style={{ 
                                      width: `${percentual}%`,
                                      backgroundColor: bairro.cor 
                                    }}
                                  ></div>
                                </div>
                                <span className="text-sm font-semibold text-gray-700 w-10">
                                  {percentual}%
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Gráfico de Tipos de Imóvel */}
            <Card className="border-0 bg-gradient-to-br from-orange-50 to-amber-50 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Building className="h-6 w-6 text-orange-600" />
                  </div>
                  Tipos de Imóvel Mais Procurados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={dadosTiposImovel}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ tipo, percentual }) => `${tipo} (${percentual}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="quantidade"
                      >
                        {dadosTiposImovel.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.cor} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

      </div>
    </Page>
  );
}
